import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import PublicHeader from "@/components/PublicHeader";
import LegalFooter from "@/components/LegalFooter";
import { ExternalLink, LayoutGrid, BookOpen, FileText, Video, Layers, Award, Image as ImageIcon, Link2 } from "lucide-react";

const KIND_META = {
  lesson:        { l: "Lesson",        icon: BookOpen },
  worksheet:     { l: "Worksheet",     icon: FileText },
  presentation:  { l: "Presentation",  icon: ImageIcon },
  quiz:          { l: "Quiz",          icon: FileText },
  demo_video:    { l: "Demo video",    icon: Video },
  activity:      { l: "Activity",      icon: Layers },
  certificate:   { l: "Certificate",   icon: Award },
  achievement:   { l: "Achievement",   icon: Award },
  project:       { l: "Project",       icon: Layers },
  resource_pack: { l: "Resource pack", icon: Layers },
  testimonial:   { l: "Testimonial",   icon: FileText },
  workshop:      { l: "Workshop",      icon: BookOpen },
  link:          { l: "Link",          icon: Link2 },
  other:         { l: "Other",         icon: Layers },
};

export default function PublicPortfolio() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/growth/public/portfolio/${username}`)
      .then(({ data }) => setData(data))
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen tb-dashboard-bg">
        <PublicHeader />
        <div className="max-w-5xl mx-auto px-5 py-16 text-center text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen tb-dashboard-bg" data-testid="public-portfolio-404">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <LayoutGrid className="h-10 w-10 mx-auto text-muted-foreground" />
          <h1 className="mt-4 font-display text-3xl tracking-tight font-medium">Portfolio not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">This educator hasn't published a portfolio yet.</p>
          <Link to="/marketplace" className="mt-6 inline-block">
            <Button className="rounded-full" data-testid="public-portfolio-discover">Discover educators</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen tb-dashboard-bg" data-testid="public-portfolio">
      <PublicHeader />
      <div className="max-w-5xl mx-auto px-5 py-10 sm:py-14">
        {/* Hero */}
        <div className="flex items-start gap-5">
          {data.photo_url ? (
            <img
              src={data.photo_url}
              alt={data.display_name}
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl object-cover border border-border"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-gradient-to-br from-violet-500/30 to-amber-400/30 grid place-items-center text-white text-2xl font-display font-medium">
              {(data.display_name || "T").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <LayoutGrid className="h-3 w-3" /> Educator Portfolio
            </span>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight font-medium" data-testid="public-portfolio-name">
              {data.display_name}
            </h1>
            {data.headline && <p className="mt-1 text-sm text-muted-foreground">{data.headline}</p>}
            {data.tagline && <p className="mt-1 text-sm italic text-foreground/80" data-testid="public-portfolio-tagline">"{data.tagline}"</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/u/${username}`}>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="public-portfolio-profile-link">View full profile</Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="rounded-full">Connect on TeeBee</Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="mt-10">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Featured work · {data.items.length}</p>
          {data.items.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">This portfolio is still being built.</p>
          ) : (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-portfolio-items">
              {data.items.map((it) => {
                const meta = KIND_META[it.kind] || KIND_META.other;
                const Icon = meta.icon;
                return (
                  <a
                    key={it.item_id}
                    href={it.url || "#"}
                    target={it.url ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                    className="tb-card p-4 hover:border-primary/40 hover:shadow-md transition group"
                    data-testid={`public-portfolio-item-${it.item_id}`}
                  >
                    {it.image_url && (
                      <img
                        src={it.image_url}
                        alt=""
                        loading="lazy"
                        className="rounded-lg w-full h-32 object-cover mb-3 bg-muted"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div className="flex items-start gap-2">
                      <span className="h-8 w-8 rounded-lg tb-chip-lavender grid place-items-center shrink-0">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{meta.l}</p>
                        <p className="text-sm font-medium leading-snug">{it.title}</p>
                      </div>
                      {it.url && <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition shrink-0" />}
                    </div>
                    {it.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{it.description}</p>}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
